import * as React from 'react';
import { toast } from 'sonner';
import { Camera, Loader2, Trash2 } from 'lucide-react';
import { Button } from '~/components/ui/button';
import { Badge, Field, Separator, Textarea } from '~/components/ui/primitives';
import { useProducts, messageOf } from '~/lib/queries';
import { api, uploadToStorage } from '~/lib/api';
import { formatPrice } from '~/lib/utils';
import type { BookingDraft } from '../booking-types';
import { StepShell } from '../step-shell';

/**
 * Tunnel de reservation — parcours principal de l'application.
 *
 * L'ordre des etapes suit litteralement l'enonce : adresse, cycle, operation de
 * maintenance, forfait, creneau, complements. Deux partis pris :
 *
 *   - La couverture est verifiee DES l'etape 1. Un client hors zone l'apprend
 *     immediatement, avant d'avoir saisi quoi que ce soit d'autre — plutot qu'apres
 *     cinq etapes, ce qui serait une perte de temps et une mauvaise experience.
 *
 *   - Le compte n'est jamais un prealable (RG-04). Un visiteur connecte se voit
 *     simplement proposer ses adresses et ses cycles enregistres.
 */

export function ExtrasStep({
  draft,
  patch,
  onBack,
  onNext,
}: {
  draft: BookingDraft;
  patch: (values: Partial<BookingDraft>) => void;
  onBack: () => void;
  onNext: () => void;
}) {
  const { data: products } = useProducts();
  const [uploading, setUploading] = React.useState(false);

  async function handleFiles(event: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(event.target.files ?? []);
    if (files.length === 0) return;

    setUploading(true);
    try {
      const keys: string[] = [];

      for (const file of files) {
        // Le fichier ne transite pas par l'API : on demande une URL signee, puis
        // on televerse directement vers le stockage objet.
        const presigned = await api.post<{ uploadUrl: string; storageKey: string }>(
          '/media/presign',
          {
            fileName: file.name,
            contentType: file.type,
            sizeBytes: file.size,
            phase: 'BEFORE',
          },
        );

        await uploadToStorage(presigned.uploadUrl, file);
        keys.push(presigned.storageKey);
      }

      patch({ mediaKeys: [...draft.mediaKeys, ...keys] });
      toast.success(
        `${keys.length} photo${keys.length > 1 ? 's' : ''} ajoutee${keys.length > 1 ? 's' : ''}.`,
      );
    } catch (error) {
      toast.error(messageOf(error));
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  }

  function setQuantity(productId: string, quantity: number) {
    const others = draft.products.filter((line) => line.productId !== productId);
    patch({ products: quantity > 0 ? [...others, { productId, quantity }] : others });
  }

  return (
    <StepShell
      title="Souhaitez-vous ajouter quelque chose ?"
      description="Ces elements sont facultatifs. Le technicien pourra en ajouter sur place."
      onBack={onBack}
      onNext={onNext}
    >
      <div className="space-y-2">
        <p className="app-section-title">Photos du probleme</p>
        <p className="text-sm text-muted-foreground">
          Une photo vaut souvent mieux qu'une description : elle permet au technicien d'emporter la
          bonne piece.
        </p>

        <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-dashed border-border p-4 transition-colors hover:bg-accent">
          <Camera className="size-5 shrink-0 text-muted-foreground" aria-hidden />
          <span className="flex-1 text-sm">
            {uploading ? 'Televersement en cours…' : 'Prendre ou choisir une photo'}
          </span>
          {uploading ? <Loader2 className="size-4 animate-spin" aria-hidden /> : null}
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            // `capture` ouvre directement l'appareil photo sur mobile.
            capture="environment"
            multiple
            className="sr-only"
            onChange={(event) => void handleFiles(event)}
            disabled={uploading}
          />
        </label>

        {draft.mediaKeys.length > 0 ? (
          <ul className="flex flex-wrap gap-2">
            {draft.mediaKeys.map((key) => (
              <li key={key}>
                <Badge variant="secondary" className="gap-1.5">
                  <Camera className="size-3" aria-hidden />
                  {key.split('/').pop()?.slice(0, 12)}…
                  <button
                    type="button"
                    onClick={() =>
                      patch({ mediaKeys: draft.mediaKeys.filter((item) => item !== key) })
                    }
                    aria-label="Retirer cette photo"
                  >
                    <Trash2 className="size-3" />
                  </button>
                </Badge>
              </li>
            ))}
          </ul>
        ) : null}
      </div>

      <Separator />

      <div className="space-y-2">
        <p className="app-section-title">Produits</p>
        <div className="flex max-h-72 flex-col gap-1 overflow-y-auto scroll-thin pr-1">
          {products?.map((product) => {
            const line = draft.products.find((item) => item.productId === product.id);

            return (
              <div
                key={product.id}
                className="app-summary-row rounded-md px-2 py-1.5 hover:bg-accent"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm">{product.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {product.category} · {formatPrice(product.price)}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <Button
                    variant="outline"
                    size="icon"
                    className="size-7"
                    onClick={() => setQuantity(product.id, Math.max(0, (line?.quantity ?? 0) - 1))}
                    disabled={!line}
                    aria-label={`Retirer un ${product.name}`}
                  >
                    −
                  </Button>
                  <span className="w-6 text-center text-sm tabular-nums" aria-live="polite">
                    {line?.quantity ?? 0}
                  </span>
                  <Button
                    variant="outline"
                    size="icon"
                    className="size-7"
                    onClick={() => setQuantity(product.id, (line?.quantity ?? 0) + 1)}
                    aria-label={`Ajouter un ${product.name}`}
                  >
                    +
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <Separator />

      <Field
        label="Precisions pour le technicien"
        htmlFor="clientNotes"
        hint="Bruit suspect, code d'immeuble, etage…"
      >
        <Textarea
          value={draft.clientNotes}
          onChange={(event) => patch({ clientNotes: event.target.value })}
          rows={3}
          maxLength={2000}
          placeholder="Le velo fait un cliquetis au pedalage. Code portail : 1234A."
        />
      </Field>
    </StepShell>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// Etape 7 — Recapitulatif
// ═══════════════════════════════════════════════════════════════════════════════
